const CART_ATTRIBUTE_KEY = 'Recipient ID / RFC / Passport';

class RequiredShippingId extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-shipping-id-input]');
    this.error = this.querySelector('[data-shipping-id-error]');

    if (!(this.input instanceof HTMLInputElement) || !(this.error instanceof HTMLElement)) return;

    this.savedValue = this.normalizedValue();
    this.onInput = () => {
      if (this.normalizedValue()) this.clearError();
      void this.persistCurrentValue().catch(() => {});
    };
    this.onChange = () => void this.persistCurrentValue().catch(() => {});
    this.onDocumentClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const checkoutTrigger = target?.closest('[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]');
      if (!checkoutTrigger) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      void this.attemptCheckout(checkoutTrigger);
    };
    this.onDocumentSubmit = (event) => {
      const submitter = event.submitter;
      if (!(submitter instanceof HTMLElement) || !submitter.matches('[name="checkout"]')) return;

      if (this.allowCheckoutOnce) {
        this.allowCheckoutOnce = false;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      void this.attemptCheckout(submitter, event.target);
    };

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('change', this.onChange);
    this.input.addEventListener('blur', this.onChange);
    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('submit', this.onDocumentSubmit, true);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.onInput);
    this.input?.removeEventListener('change', this.onChange);
    this.input?.removeEventListener('blur', this.onChange);
    document.removeEventListener('click', this.onDocumentClick, true);
    document.removeEventListener('submit', this.onDocumentSubmit, true);
  }

  normalizedValue() {
    return this.input?.value.trim() ?? '';
  }

  clearError() {
    this.classList.remove('is-invalid');
    this.input?.removeAttribute('aria-invalid');
    if (this.error) this.error.hidden = true;
  }

  showError() {
    this.classList.add('is-invalid');
    this.input?.setAttribute('aria-invalid', 'true');
    if (this.error) this.error.hidden = false;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    this.input?.focus({ preventScroll: true });
  }

  async persistCurrentValue() {
    this.queuedValue = this.normalizedValue();

    if (!this.persistencePromise) {
      this.persistencePromise = this.drainPersistenceQueue().finally(() => {
        this.persistencePromise = null;
      });
    }

    await this.persistencePromise;
  }

  async drainPersistenceQueue() {
    while (this.queuedValue !== undefined) {
      const value = this.queuedValue;
      this.queuedValue = undefined;

      if (this.savedValue === value) continue;

      await this.updateCartAttribute(value);
      this.savedValue = value;
    }
  }

  async updateCartAttribute(value) {
    const root = window.Shopify?.routes?.root ?? '/';
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ attributes: { [CART_ATTRIBUTE_KEY]: value } }),
    });

    if (!response.ok) throw new Error('Unable to persist required cart attribute.');
    await response.json();
  }

  async attemptCheckout(trigger, form = document.getElementById('cart-form')) {
    if (!this.normalizedValue()) {
      this.showError();
      return;
    }

    this.clearError();

    try {
      await this.persistCurrentValue();
    } catch {
      this.showError();
      return;
    }

    if (trigger instanceof HTMLButtonElement && form instanceof HTMLFormElement) {
      this.allowCheckoutOnce = true;
      form.requestSubmit(trigger);
      return;
    }

    const root = window.Shopify?.routes?.root ?? '/';
    window.location.assign(`${root}checkout`);
  }
}

if (!customElements.get('required-shipping-id')) {
  customElements.define('required-shipping-id', RequiredShippingId);
}
